import { useEffect, useRef, useCallback } from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import Operations from '../../../../operations'
import Actions from '../../../../actions'
import Selectors from '../../../../selectors'
import dayjs from 'dayjs'
import { isBrowser, isTablet } from 'react-device-detect'
import { gaEvent } from '../../../../common/services/analytics'
import { getNominative } from '../../../../common/utils'
import { colors, sizes } from '@lmc/common/constants'
import { Spinner } from '../../../../components/generic'
import SeenBySection from '../../../Residents/screens/ResidentRiskAssessmentsScreens/components/SeenBySection'
import RiskAssessmentItem from '../../../Residents/screens/ResidentRiskAssessmentsScreens/components/RiskAssessmentItem'
import {
  CarePlanNextReviewField,
  CarePlanSummaryHeader,
  CarePlanSectionContent,
  CarePlanSectionHeader,
  CarePlanDocuments
} from '../../../../components/carePlans'
import Signatures from '../../components/CarePlanSignatures'
import { FormCard, Heading, Button } from '@lmc/ui-kit'
import { withRouter } from '../../../../withRouter'
import { useLocation } from 'react-router-dom'
import { history } from '../../../../store'
import { Badge } from '@lmc/ui-kit'
import CarePlanGoalsListInput from './components/CarePlansGoalsListInput/CarePlanGoalsListInput'
import { CarePlanSectionContainer } from '../../components/CarePlanSections'
import { CAREPLAN_ADL_PBS_ID } from '@lmc/common/types'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import FluidTrackingCard from './components/FluidTrackingCard/FluidTrackingCard'
import { useCarePlanPopulated } from '../../hooks/useCarePlanPopulated'
import ConsentSection from '../CarePlanReviewScreen/components/ConsentSection/ConsentSection'
import NeedsSection from '../CarePlanReviewScreen/components/NeedsSection/NeedsSection'
import useFeatureFlags from '../../../../hooks/useFeatureFlags'

const EATING_AND_DRINKING_ADL = '5d3ef8e7610ca2e6713c60f0'

const Section = ({ color, icon, title, content, innerComponent }) => (
  <div style={styles.sectionContainer}>
    <CarePlanSectionHeader color={color} icon={icon} title={title} />
    <div
      style={{
        ...styles.innerSectionContainer,
        background: !innerComponent ? 'transparent' : 'white'
      }}
    >
      {innerComponent}
      <CarePlanSectionContent value={content} />
    </div>
  </div>
)

export const CarePlanViewScreen = ({
  features,
  fetchNeedsOptions,
  fetchSkillNeedTemplates,
  router,
  signatureItem,
  carers,
  setLoadedListItem,
  carePlanSignatures,
  residents,
  carePlanNominative,
  needsOptions,
  hasGoals,
  pbsFlagEnabled,
  fluidTrackingFlagEnabled,
  hasAccess,
  documents: allDocuments
}) => {
  const formRef = useRef(null)
  const gaEventTriggered = useRef(false)
  const location = useLocation()
  const referrer = location.state?.referrer

  const flags = useFeatureFlags({ flagNames: ['informed_consent', 'mods_needs_care_plan'] })?.data
  const informedConsentFlagEnabled = flags?.informed_consent?.enabled || false
  const needsCarePlanFlagEnabled = flags?.mods_needs_care_plan?.enabled || false

  const {
    params: { id, care_plan_id, revision_id }
  } = router

  const chosenId = revision_id || care_plan_id
  // Derive isRevision from URL - if revision_id exists, we're viewing a historical revision
  const isViewingRevision = !!revision_id

  const { data: carePlan, isLoading } = useCarePlanPopulated({
    carePlanId: chosenId,
    enabled: !!chosenId && !!id
  })

  useEffect(() => {
    if (carePlan && !gaEventTriggered.current) {
      gaEvent({
        category: 'CarePlans',
        action: 'View',
        label: carePlan.carePlanAdl?.name
      })
      gaEventTriggered.current = true
    }
  }, [carePlan])

  useEffect(() => {
    if (features?.features?.skillsAndNeeds?.enabled) {
      fetchNeedsOptions?.()
      fetchSkillNeedTemplates?.()
    }
  }, [
    features?.features?.skillsAndNeeds?.enabled,
    fetchNeedsOptions,
    fetchSkillNeedTemplates
  ])

  const handleRiskAssessmentClick = useCallback(
    (riskId) => {
      formRef.current?.scrollIntoView()
      window.scrollTo(0, 0)
      history.push(`/residents/${id}/risk-assessments/${riskId}`)
    },
    [id]
  )

  const onEdit = useCallback(() => {
    gaEvent({
      category: 'CarePlans',
      action: 'Edit',
      label: carePlan?.carePlanAdl?.name
    })
    history.push(
      `/residents/${id}/care-plans/${care_plan_id}/edit`,
      location.state
    )
  }, [id, care_plan_id, carePlan?.carePlanAdl?.name, location])

  if (isLoading || !id || !carePlan || !carers || !residents) {
    return <Spinner />
  }

  const resident = _.find(residents, { id })
  const editedByCarer = carePlan.editedBy
    ? _.find(carers, { id: carePlan.editedBy })
    : null
  const preferredCarePlanName = getNominative(carePlanNominative, 'Care Plan')
  const skillNeeds = carePlan?.skillNeeds
  const isPBSAssessment =
    pbsFlagEnabled && carePlan.carePlanAdl?.id === CAREPLAN_ADL_PBS_ID
  const hasRolePermission = hasAccess(
    ['carehome-admin', 'senior-carer'],
    [
      {
        i: 'update',
        this: 'CarePlan'
      }
    ]
  )
  const baseUrl = `/residents/${id}/care-plans`
  const logs = carePlan.logs || []
  const populatedDocuments = carePlan.populatedDocuments || []
  const residentDocuments = _.filter(allDocuments ?? [], { resident: id }) ?? []
  const documents = _.uniqBy(
    [...(residentDocuments || []), ...(populatedDocuments || [])],
    'id'
  ).sort((a, b) => (a.id || '').localeCompare(b.id || ''))
  const riskAssessments = carePlan.populatedRiskAssessments || []
  const fluidTrackingEnabled = carePlan?.fluidTracking?.enabled

  const renderHeader = () => (
    <CarePlanSummaryHeader
      carePlan={{ ...carePlan, isRevision: isViewingRevision }}
      carer={editedByCarer}
      resident={resident}
      logs={logs}
      onClickBack={() => {
        if (isViewingRevision) {
          // When viewing a revision, go back to the history list
          router.navigate(`${baseUrl}/${care_plan_id}/history`)
        } else {
          // When viewing active care plan, go to care plans list
          router.navigate(referrer || baseUrl)
        }
      }}
      onClickHistory={() =>
        router.navigate(`${baseUrl}/${care_plan_id}/history`)
      }
      carePlanNominative={carePlanNominative}
    />
  )

  const renderRisks = () => {
    let Risks = null

    if (riskAssessments && riskAssessments.length) {
      Risks = _.sortBy(riskAssessments, 'form.name').map((row) => {
        const hasActions = !!row?.form?.hasActions && !!row?.formData?.actions

        return (
          <div key={row.id}>
            <RiskAssessmentItem
              carer={row.createdBy}
              showUpdateAlert={dayjs(carePlan.editedOn).isBefore(row.createdOn)}
              riskAssessment={row}
              onView={handleRiskAssessmentClick}
              carePlanNominative={carePlanNominative}
            />
            {hasActions && (
              <WrapperActions>
                <StyledHeading level={3}>
                  Actions for {row?.form?.name}
                </StyledHeading>
                <StyledText>{row?.formData?.actions}</StyledText>
              </WrapperActions>
            )}
          </div>
        )
      })
    }

    const hasRisks = !!Risks?.length

    return (
      <div style={styles.sectionContainer}>
        <FormCard title="Risks">
          {hasRisks ? <div style={styles.risksSection}>{Risks}</div> : null}
        </FormCard>
      </div>
    )
  }

  // Render documents
  const renderDocuments = () => (
    <div style={styles.sectionContainer}>
      <CarePlanDocuments carePlan={carePlan} documents={documents} />
    </div>
  )

  const renderFluidTracking = () => {
    if (
      carePlan.carePlanAdl?.id !== EATING_AND_DRINKING_ADL ||
      !fluidTrackingFlagEnabled ||
      !fluidTrackingEnabled
    ) {
      return null
    }

    return (
      <div style={styles.sectionContainer}>
        <FluidTrackingCard
          resident={resident}
          carePlan={carePlan}
          fluidTrackingFlagEnabled={fluidTrackingFlagEnabled}
        />
      </div>
    )
  }

  const renderNextReview = () => (
    <div style={styles.sectionContainer}>
      <CarePlanNextReviewField
        date={carePlan.nextReviewDate}
        noFurtherSupportRequired={carePlan.noFurtherSupportRequired}
        style={{
          marginBottom: 25,
          marginTop: 25
        }}
      />
    </div>
  )

  const renderSeenBy = () => {
    if (!carePlan.seenBy || !carePlan.seenBy.length) return null

    const filteredAvatars = carePlan.seenBy
      .filter((seenByItem) => seenByItem)
      .map((seenByItem) => {
        const carerId = seenByItem.id || seenByItem
        const carer = _.find(carers, { id: carerId })
        return {
          name: carer?.name
            ? `${carer.name.first || ''} ${carer.name.last || ''}`.trim()
            : '',
          viewedOn: seenByItem.viewedOn || '',
          url: carer?.picture
        }
      })
      .filter((avatar) => avatar.name)

    return <SeenBySection filteredAvatars={filteredAvatars} />
  }

  return (
    <div ref={formRef} style={styles.wrapper}>
      {renderHeader()}
      {isPBSAssessment && (
        <CarePlanSectionContainer sections={carePlan?.sections} isViewMode />
      )}
      {!isPBSAssessment && (
        <Section
          color={colors.green}
          icon="calendar"
          title="Strengths"
          content={carePlan.strengths}
        />
      )}
      {!isPBSAssessment && needsCarePlanFlagEnabled && (
        <div style={styles.sectionContainer}>
          <NeedsSection
            residentId={id}
            residentNeeds={carePlan.residentNeeds || []}
            residentNeedsSnapshot={carePlan.residentNeedsSnapshot || []}
            value={carePlan.residentNeedIds || []}
            isRevision={isViewingRevision}
            disabled
            lastReviewDate={carePlan.editedOn}
            notesValue={carePlan.needs}
            skillNeedsContent={
              features?.features?.skillsAndNeeds?.enabled &&
              skillNeeds?.length ? (
                <div style={styles.needItem}>
                  {skillNeeds.map((skillNeed) => (
                    <Badge
                      key={skillNeed}
                      color={colors.gray}
                      text={
                        needsOptions?.find(
                          (needOption) => needOption.id === skillNeed
                        )?.name
                      }
                      textColor={colors.white}
                      style={{
                        maxWidth: 'fit-content',
                        marginTop: 5,
                        marginBottom: 5
                      }}
                    />
                  ))}
                </div>
              ) : null
            }
          />
        </div>
      )}
      {!isPBSAssessment && !needsCarePlanFlagEnabled && (
        <Section
          color={colors.orange}
          icon="calendar"
          title="Needs"
          content={carePlan.needs}
          innerComponent={
            features?.features?.skillsAndNeeds?.enabled &&
            skillNeeds?.length ? (
              <div style={styles.needItem}>
                {skillNeeds.map((skillNeed) => (
                  <Badge
                    key={skillNeed}
                    color={colors.gray}
                    text={
                      needsOptions?.find(
                        (needOption) => needOption.id === skillNeed
                      )?.name
                    }
                    textColor={colors.white}
                    style={{
                      maxWidth: 'fit-content',
                      marginTop: 5,
                      marginBottom: 5
                    }}
                  />
                ))}
              </div>
            ) : null
          }
        />
      )}
      {renderRisks()}
      {hasGoals && (
        <CarePlanGoalsListInput disabled value={carePlan.goals || []} />
      )}
      {renderFluidTracking()}
      {renderDocuments()}
      {informedConsentFlagEnabled && (
        <div style={styles.sectionContainer}>
          <ConsentSection
            residentId={id}
            consentForms={carePlan.consentForms}
            consentFormsSnapshot={carePlan.consentFormsSnapshot}
            isRevision={isViewingRevision}
            disabled
          />
        </div>
      )}
      {renderNextReview()}
      {renderSeenBy()}
      {carePlanSignatures && signatureItem && (
        <Signatures
          carePlan={carePlan}
          logs={logs}
          carers={carers}
          resident={resident}
          setLoadedListItem={setLoadedListItem}
          preferredCarePlanName={preferredCarePlanName}
          item={signatureItem.id}
          category={signatureItem.category}
          informedConsentEnabled={informedConsentFlagEnabled}
        />
      )}
      {!isViewingRevision && (
        <div
          className="lmc-fixed-footer"
          style={{
            ...styles.footer,
            width: isBrowser || isTablet ? 'calc(100vw - 290px)' : '100vw'
          }}
        >
          <Button
            id="review-care-plan"
            dataTestId="cp-review"
            onClick={onEdit}
            style={styles.updateButton}
            variant="primary"
            disabledByPermission={!hasRolePermission}
          >
            {`Review ${preferredCarePlanName}`}
          </Button>
        </div>
      )}
    </div>
  )
}

const styles = {
  innerSectionContainer: {
    borderRadius: 15
  },
  needItem: {
    marginLeft: 15,
    paddingTop: 15,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  needTextItem: { color: colors.black, marginBottom: 0 },
  wrapper: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    paddingTop: 24,
    paddingBottom: 80
  },
  editButton: {
    float: 'right'
  },
  divider: {
    display: 'flex',
    height: 2
  },
  footer: {
    border: 'none',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  subHeading: {
    fontSize: 16,
    opacity: 0.8
  },
  sectionContainer: {
    paddingTop: 30
  },
  sectionContent: {
    paddingTop: 15,
    minHeight: 100,
    whiteSpace: 'pre-wrap'
  },
  actionsSection: {
    paddingTop: 15,
    minHeight: 100,
    whiteSpace: 'pre-wrap'
  },
  risksSection: {
    paddingTop: 5,
    minHeight: 100
  },
  updateButton: {
    color: 'white',
    textShadow: 'none'
  }
}

const WrapperActions = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  margin-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 15px;
`

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color4};
  border-bottom: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color8};
  margin-bottom: 10px;
  padding-bottom: 10px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  white-space: pre-wrap;
`

const mapStateToProps = (state) => ({
  signatureItem: _.find(state.data['log-category-items'], {
    itemId: 'signature'
  }),
  carePlanSignatures: state.global.home.features?.carePlanSignatures?.enabled,
  carers: state.data.carers,
  residents: state.data.residents,
  documents: state.data.documents,
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  needsOptions: state.data['skill-need-templates/needs'],
  features: state.global.home,
  hasGoals: state.global.home.features?.goals?.enabled,
  hasAccess: (roles, abilities) =>
    Selectors.checkRoleAccess(state, { roles, abilities })
})

const mapDispatchToProps = (dispatch) => ({
  setLoadedListItem: (listId, item) =>
    dispatch(Actions.setLoadedListItem(listId, item)),
  fetchNeedsOptions: () =>
    dispatch(Operations.loadList('skill-need-templates/needs')),
  fetchSkillNeedTemplates: () =>
    dispatch(Operations.loadList('skill-need-templates'))
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CarePlanViewScreen)
)
